import React from 'react';
import { useTranslation } from 'react-i18next';
import i18n, { currentLocale } from '../../../i18n';
import { dateLocale } from '../../../lib/localePrefs';
import { SEV_META } from '../data/insights';
import type {
  SubscriptionFeature,
  SubscriptionFeatureKey,
  SubscriptionRestaurant,
  SubscriptionStatus,
  SubscriptionUsagePayload,
} from '../types';
import { Icon } from './shared/Icon';

// Matrix column order; mirrors FEATURE_ORDER in subscription_usage.py.
const FEATURE_ORDER: SubscriptionFeatureKey[] = [
  'pos',
  'channelManager',
  'pms',
  'pmsAutomation',
  'alenoPayNoShow',
  'alenoPayTransactions',
  'insights',
  'sms',
  'respondelligent',
  'upgrade',
  'intelligence',
  'licence',
  'other',
];

// Venues named in the group-billed note before it trails off.
const GROUP_BILLED_NAMES = 6;

// Matrix cell tone, reusing the settings matrix's sm-cell states.
const CELL_TONE: Record<SubscriptionStatus, string | undefined> = {
  inUse: 'ok',
  partial: 'warn',
  unused: 'alert',
  included: undefined,
  unchecked: undefined,
};

export type SubscriptionIssueKey =
  | 'pos'
  | 'channelManagerUnused'
  | 'channelManagerPartial'
  | 'pms'
  | 'pmsAutomation'
  | 'alenoPayNoShow'
  | 'alenoPayTransactions'
  | 'insights'
  | 'sms';

export type SubscriptionIssue = {
  key: SubscriptionIssueKey;
  partial: boolean;
  affected: { restaurant: SubscriptionRestaurant; feature: SubscriptionFeature }[];
};

function issueKeyFor(feature: SubscriptionFeature): SubscriptionIssueKey | null {
  if (feature.key === 'channelManager') {
    if (feature.status === 'unused') return 'channelManagerUnused';
    if (feature.status === 'partial') return 'channelManagerPartial';
    return null;
  }
  if (feature.status !== 'unused') return null;
  switch (feature.key) {
    case 'pos':
    case 'pms':
    case 'pmsAutomation':
    case 'alenoPayNoShow':
    case 'alenoPayTransactions':
    case 'insights':
    case 'sms':
      return feature.key;
    default:
      return null;
  }
}

/** Paid features that go unused, one card per problem across venues;
 *  products nobody uses come before ones partly used. */
export function subscriptionIssues(usage: SubscriptionUsagePayload): SubscriptionIssue[] {
  const byKey = new Map<SubscriptionIssueKey, SubscriptionIssue>();
  for (const restaurant of usage.restaurants) {
    for (const feature of restaurant.features) {
      const key = issueKeyFor(feature);
      if (!key) continue;
      let issue = byKey.get(key);
      if (!issue) {
        issue = { key, partial: feature.status === 'partial', affected: [] };
        byKey.set(key, issue);
      }
      issue.affected.push({ restaurant, feature });
    }
  }
  return [...byKey.values()].sort((a, b) => Number(a.partial) - Number(b.partial));
}

/** Whether aleno can tell if the product is used: the licence and aleno
 *  intelligence are what the customer is using right now, and some
 *  products have nothing in aleno to check. */
function isChecked(feature: SubscriptionFeature): boolean {
  return feature.status !== 'included' && feature.status !== 'unchecked';
}

/** Checked features billed to at least one venue, in display order. */
export function subscriptionColumns(usage: SubscriptionUsagePayload): SubscriptionFeatureKey[] {
  const present = new Set(
    usage.restaurants.flatMap(r => r.features.filter(isChecked).map(f => f.key)),
  );
  return FEATURE_ORDER.filter(key => present.has(key));
}

/** Products on a contract that aleno has no data to check, by name. */
export function subscriptionUnchecked(usage: SubscriptionUsagePayload): string[] {
  const names = new Set<string>();
  for (const restaurant of usage.restaurants) {
    for (const feature of restaurant.features) {
      if (feature.status === 'unchecked') names.add(featureLabel(feature.key, feature));
    }
  }
  return [...names];
}

function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString(dateLocale(currentLocale()), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** The data behind a feature's status, as one sentence. */
export function subscriptionEvidence(feature: SubscriptionFeature, days: number): string {
  const t = (key: string, opts?: Record<string, unknown>) =>
    i18n.t(`settings:subscription.evidence.${key}`, opts);
  switch (feature.key) {
    case 'pos':
      if (feature.lastUsedAt == null) return t('posNever');
      return feature.status === 'inUse'
        ? t('posRecent', { date: formatDate(feature.lastUsedAt) })
        : t('posSince', { date: formatDate(feature.lastUsedAt) });
    case 'channelManager':
      return feature.reservations
        ? t('channelReservations', {
            count: feature.reservations,
            rwg: feature.rwgReservations ?? 0,
            days,
          })
        : t('channelNone', { days });
    case 'pms':
      if (feature.hotelStays) return t('pmsStays', { count: feature.hotelStays, days });
      if (feature.reservations) {
        return t('pmsReservations', { count: feature.reservations, days });
      }
      return feature.switchedOn ? t('pmsNoStaysOn', { days }) : t('pmsNoStaysOff', { days });
    case 'pmsAutomation':
      return feature.reservations
        ? t('pmsRuleReservations', { count: feature.reservations, days })
        : t('pmsRuleNone', { days });
    case 'alenoPayNoShow':
      return feature.status === 'inUse' ? t('noShowOn') : t('noShowOff');
    case 'alenoPayTransactions':
      return feature.status === 'inUse' ? t('payOn') : t('payOff');
    case 'insights':
      return feature.status === 'inUse' ? t('insightsOn') : t('insightsOff');
    case 'sms':
      return feature.status === 'inUse' ? t('smsOn') : t('smsOff');
    case 'licence':
    case 'intelligence':
      return t('included');
    default:
      return t('unchecked');
  }
}

function featureLabel(key: SubscriptionFeatureKey, feature?: SubscriptionFeature): string {
  // Unknown products go by the name they are billed under.
  if (key === 'other' && feature?.items.length) return feature.items.join(', ');
  return i18n.t(`settings:subscription.feature.${key}`);
}

function IssueCards({ issues, days, multi }: {
  issues: SubscriptionIssue[];
  days: number;
  multi: boolean;
}) {
  const { t } = useTranslation('settings');
  return (
    <div className="sm-issues">
      {issues.map(issue => {
        const sev = issue.partial ? SEV_META.opportunity : SEV_META.alert;
        const count = issue.affected.length;
        return (
          <div key={issue.key} className="ins-card sm-issue" data-testid={`sub-issue-${issue.key}`}>
            <div className="sm-issue-head">
              <span className="sm-issue-cat">
                <Icon name={sev.icon} size={14} fill style={{ color: sev.c }} />
                {t(`subscription.issue.${issue.key}.title`)}
              </span>
            </div>
            <div className="sm-issue-impact">
              {t(`subscription.issue.${issue.key}.impact`, { count, days })}
              {count === 1 && (
                <>
                  {' '}
                  {multi ? `${issue.affected[0].restaurant.restaurantName}: ` : ''}
                  {subscriptionEvidence(issue.affected[0].feature, days)}.
                </>
              )}
            </div>
            <div className="ins-action">
              <Icon name="check" size={13} stroke={2.3} style={{ marginTop: 1, color: sev.c }} />
              <span>{t(`subscription.issue.${issue.key}.action`)}</span>
            </div>
            {count > 1 && (
              <details className="sm-affected">
                <summary>{t('subscription.where')}</summary>
                <div className="sm-affected-list">
                  {issue.affected.map(({ restaurant, feature }) => (
                    <span
                      key={restaurant.restaurantId}
                      className="sm-chip"
                      title={subscriptionEvidence(feature, days)}
                    >
                      {restaurant.restaurantName}
                    </span>
                  ))}
                </div>
              </details>
            )}
          </div>
        );
      })}
    </div>
  );
}

function UsageMatrix({ usage, columns }: {
  usage: SubscriptionUsagePayload;
  columns: SubscriptionFeatureKey[];
}) {
  const { t } = useTranslation('settings');
  // A venue paying only for products aleno can't check has nothing to show.
  const rows = usage.restaurants.filter(r => r.features.some(isChecked));
  return (
    <div className="sm-scroll">
      <table className="sm-table">
        <thead>
          <tr>
            <th className="sm-th-out su-th-out">{t('subscription.venue')}</th>
            {columns.map(key => (
              <th key={key} className="su-th">
                {featureLabel(key)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(restaurant => {
            const byKey = new Map(restaurant.features.map(f => [f.key, f]));
            return (
              <tr key={restaurant.restaurantId} className="sm-row">
                <td>
                  <div className="sm-out">
                    <div className="sm-out-main">
                      <b>{restaurant.restaurantName}</b>
                    </div>
                  </div>
                </td>
                {columns.map(key => {
                  const feature = byKey.get(key);
                  if (!feature) {
                    return (
                      <td key={key} title={t('subscription.status.notBilled')}>
                        <span className="su-none">{'–'}</span>
                      </td>
                    );
                  }
                  const evidence = subscriptionEvidence(feature, usage.windowDays);
                  return (
                    <td key={key}>
                      <span
                        className="sm-cell su-cell"
                        data-st={CELL_TONE[feature.status]}
                        data-tip
                        title={evidence}
                      >
                        {t(`subscription.status.${feature.status}`)}
                      </span>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Settings > Subscription: what each venue pays for, and whether it uses it. */
export function SubscriptionUsagePanel({ usage }: { usage?: SubscriptionUsagePayload }) {
  const { t } = useTranslation('settings');
  if (!usage || (!usage.restaurants.length && !usage.withoutSubscription.length)) {
    return null;
  }
  const days = usage.windowDays;
  const issues = subscriptionIssues(usage);
  const columns = subscriptionColumns(usage);
  const unchecked = subscriptionUnchecked(usage);
  const multi = usage.restaurants.length > 1;
  const without = usage.withoutSubscription;

  return (
    <section data-testid="subscription-usage">
      <div className="sec-head" style={{ marginTop: 30 }}>
        <h2>{t('subscription.title')}</h2>
        <span className="sh-meta">{t('subscription.meta', { days })}</span>
        <span className="prov-note" title={t('subscription.billingTip', { days })}>
          {t('subscription.fromBilling')}
        </span>
      </div>

      {issues.length > 0 ? (
        <>
          <div className="sm-subhead">
            {t('subscription.flaggedHead', { count: issues.length })}
          </div>
          <IssueCards issues={issues} days={days} multi={multi} />
        </>
      ) : usage.restaurants.length > 0 ? (
        <div className="su-ok">
          <Icon name="check" size={13} stroke={2.3} style={{ color: 'var(--st-steady)' }} />
          {t(unchecked.length ? 'subscription.allCheckedInUse' : 'subscription.allInUse')}
        </div>
      ) : null}

      {columns.length > 0 && (
        <>
          <div className="sm-subhead">{t('subscription.overview')}</div>
          <div className="ol-card sm-card">
            <UsageMatrix usage={usage} columns={columns} />
          </div>
        </>
      )}

      {unchecked.length > 0 && (
        <p className="su-note" data-testid="sub-unchecked">
          {t('subscription.notChecked', { count: unchecked.length, names: unchecked.join(', ') })}
        </p>
      )}

      {without.length > 0 && (
        <p className="su-note">
          {t('subscription.groupBilled', {
            count: without.length,
            names:
              without
                .slice(0, GROUP_BILLED_NAMES)
                .map(r => r.restaurantName)
                .join(', ') + (without.length > GROUP_BILLED_NAMES ? ', \u2026' : ''),
          })}
        </p>
      )}
    </section>
  );
}
